import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { ChartData, ChartTooltipItem } from "chart.js";
import { merge } from "lodash";
import { environment as appEnv } from "../../../environments/environment";
import { ChartHelperService } from "../../chart/chart-helper.service";
import { TicksAgoService } from "../../chart/ticks-ago.service";
import { CSSColorService } from "../../css-color/css-color.service";
import { DurationFormatService } from "../../duration-format/duration-format.service";
import { AccountService } from "../../models/account.service";

@Component({
  selector: "ab-route-charts",
  templateUrl: "./route-charts.component.html",
})
export class RouteChartsComponent implements OnChanges, OnInit {
  @Input() public routesStats: any;
  @Input() public statusCode: any;
  @Input() public loading: boolean;
  @Input() public project: any;
  @Input() public dur: string;
  @Input() public lastN: number;

  public appEnv = appEnv;
  public totalRequestsChartConfig: any;
  public timingChartConfig: any;
  public errorsChartConfig: any;
  public apdexChartConfig: any;
  public apdexChartTooltip: any;
  public accountApmCapReached: boolean;

  constructor(
    private accountService: AccountService,
    private chartHelperService: ChartHelperService,
    private cssColorService: CSSColorService,
    private durationFormatService: DurationFormatService,
    private ticksAgoService: TicksAgoService,
  ) {}

  public ngOnInit() {
    this.accountService.properties().subscribe((props: any) => {
      this.accountApmCapReached = props.alerts.apm_cap_alert === true;
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.statusCode == null || this.routesStats == null) {
      return;
    }

    this.apdexChartTooltip = this.apdexChartTitleTooltip();

    const ticks = this.ticksAgoService.ticksFor(this.dur, this.lastN);

    const serverErrorBgColor = this.cssColorService.getColor("--ab-red-400");
    const statusCodeGroups = [
      {
        label: "Total",
        value: "total",
        backgroundColor: this.cssColorService.getColor("--ab-blue-400"),
        borderColor: this.cssColorService.getColor("--ab-blue-400"),
      },
      {
        label: "2xx",
        value: "success",
        backgroundColor: this.cssColorService.getColor("--ab-green-400"),
        borderColor: this.cssColorService.getColor("--ab-green-400"),
      },
      {
        label: "3xx",
        value: "redirection",
        backgroundColor: this.cssColorService.getColor("--ab-grey-400"),
        borderColor: this.cssColorService.getColor("--ab-grey-400"),
      },
      {
        label: "4xx",
        value: "clientError",
        backgroundColor: this.cssColorService.getColor("--ab-orange-400"),
        borderColor: this.cssColorService.getColor("--ab-orange-400"),
      },
      {
        label: "5xx",
        value: "serverError",
        backgroundColor: serverErrorBgColor, // ab-red-400
        borderColor: this.cssColorService.getColor("--ab-red-400"), // ab-red-400
      },
    ];

    const time = this.routesStats.time.map((d: string) => new Date(d));

    const datasets = [];

    for (const group of statusCodeGroups) {
      let label;
      if (this.statusCode.name[0] === group.label[0]) {
        label = this.statusCode.name;
      } else {
        label = group.label;
      }

      const showLine =
        this.statusCode.name === "total" ||
        this.statusCode.name === group.value ||
        this.statusCode.name[0] === group.label[0];
      const data =
        this.statusCode.name[0] === group.label[0]
          ? this.statusCode.count
          : this.routesStats[group.value].count;
      const pointHoverRadius = showLine ? 2 : 0;

      datasets.push({
        label,
        backgroundColor: group.backgroundColor,
        borderColor: group.borderColor,
        pointHoverRadius,
        showLine,
        data: this.normalizeValues(data, time, ticks),
        yAxisID: group.label === "5xx" ? "right" : "left",
      });
    }

    this.totalRequestsChartConfig = {
      data: {
        labels: ticks,
        datasets,
      },
      options: merge({}, this.chartHelperService.defaultChartOptions(), {
        scales: {
          yAxes: [
            {
              id: "left",
              position: "left",
            },
            {
              id: "right",
              position: "right",
              gridLines: {
                color: serverErrorBgColor, // color the vertical y-axis line red
                display: false, // hide the horizontal gridlines
              },
              ticks: {
                fontColor: serverErrorBgColor,
                padding: -4,
                min: 0,
                precision: 0,
              },
              scaleLabel: {
                display: true,
                fontColor: serverErrorBgColor,
                labelString: "5xx",
                lineHeight: 1.5,
                padding: -4,
              },
            },
          ],
        },
      }),
    };

    this.timingChartConfig = {
      data: {
        labels: ticks,
        datasets: [
          {
            label: "Mean",
            backgroundColor: this.cssColorService.getColor("--ab-orange-400"),
            borderColor: this.cssColorService.getColor("--ab-orange-400"),
            data: this.normalizeValues(this.statusCode.mean || [], time, ticks),
          },
          {
            fill: false,
            label: "50th Percentile",
            backgroundColor: this.cssColorService.getColor("--ab-green-400"),
            borderColor: this.cssColorService.getColor("--ab-green-400"),
            data: this.normalizeValues(this.statusCode.p50 || [], time, ticks),
          },
          {
            fill: false,
            label: "95th Percentile",
            backgroundColor: this.cssColorService.getColor("--ab-blue-400"),
            borderColor: this.cssColorService.getColor("--ab-blue-400"),
            data: this.normalizeValues(this.statusCode.p95 || [], time, ticks),
          },
        ],
      },
      options: merge({}, this.chartHelperService.defaultChartOptions(), {
        scales: {
          yAxes: [
            {
              ticks: {
                callback: (value: number) => {
                  if (value % 1 === 0) {
                    return this.durationFormatService.format(value).join("");
                  }
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem: ChartTooltipItem, data: ChartData) => {
              const label = data.datasets[tooltipItem.datasetIndex].label;
              const value = this.durationFormatService
                .format(Number(tooltipItem.yLabel))
                .join("");
              return `${label}: ${value}`;
            },
          },
        },
      }),
    };

    this.errorsChartConfig = {
      data: {
        labels: ticks,
        datasets: [
          {
            label: "Error occurrences",
            backgroundColor: this.cssColorService.getColor("--ab-red-400"),
            borderColor: this.cssColorService.getColor("--ab-red-400"),
            data: this.normalizeValues(
              this.routesStats.noticeCount || [],
              time,
              ticks,
            ),
          },
        ],
      },
      options: this.chartHelperService.defaultChartOptions(),
    };

    this.apdexChartConfig = {
      data: {
        labels: ticks,
        datasets: [
          {
            label: "Satisfied",
            backgroundColor: this.cssColorService.getColor("--ab-green-400"),
            borderColor: this.cssColorService.getColor("--ab-green-400"),
            data: this.normalizeValues(
              this.statusCode.satisfied || [],
              time,
              ticks,
            ),
          },
          {
            label: "Tolerating",
            backgroundColor: this.cssColorService.getColor("--ab-orange-400"),
            borderColor: this.cssColorService.getColor("--ab-orange-400"),
            data: this.normalizeValues(
              this.statusCode.tolerating || [],
              time,
              ticks,
            ),
          },
          {
            label: "Frustrated",
            backgroundColor: this.cssColorService.getColor("--ab-red-400"),
            borderColor: this.cssColorService.getColor("--ab-red-400"),
            data: this.normalizeValues(
              this.statusCode.frustrated || [],
              time,
              ticks,
            ),
          },
        ],
      },
      options: this.chartHelperService.defaultChartOptions(),
    };
  }

  private normalizeValues(counts: any, time: string[], ticks: Date[]) {
    const normalVals = [];
    const valueTimes = time.map((d) => new Date(d).getTime());
    for (const t of ticks) {
      let val = 0;
      const routeDataIndex = valueTimes.indexOf(t.getTime());
      if (routeDataIndex > -1 && counts[routeDataIndex] != null) {
        val = counts[routeDataIndex];
      }
      normalVals.push(val);
    }
    return normalVals;
  }

  private apdexChartTitleTooltip() {
    const apdexT = this.project.apdexThreshold;

    const tooltip =
      "User satisfaction levels are calculated based on your Apdex " +
      `Threshold value (${apdexT} s)`;

    if (this.project.apdexThreshold > 0) {
      return (
        tooltip +
        " which means requests responding in less than " +
        `${apdexT} s are satisfying, responding between ${apdexT} s and ` +
        `${4 * apdexT} s are tolerating, and responding in more than ` +
        `${4 * apdexT} s are frustrating`
      );
    } else {
      return tooltip;
    }
  }
}
